import { gql, useLazyQuery } from '@apollo/client'

import agentClient from '@/lib/apollo/client/agent'
import type { WrappedUseLazyQuery } from '@/lib/apollo/types'

import type { ReferenceTier } from './get-applicant'

const getFraudSubjectsQuery = gql`
  query (
    $customerId: String!
    $filter: FraudFilterOptions!
    $limit: Int!
    $offset: Int!
  ) {
    getFraudSubjects(
      customerId: $customerId
      filter: $filter
      limit: $limit
      offset: $offset
    ) {
      limit
      offset
      total
      results {
        customerId
        applicationId
        subjectId
        customerClientName
        name
        email
        avatar
        type
        referenceTier
        address {
          nameOrNumber
          line1
          line2
          city
          country
          postcode
        }
        milestones {
          createdAt
          event
        }
        tenancyStartDate
        visitors
        flaggedAsFraudAt
      }
    }
  }
`

enum AgeOfReference {
  ALL = 'all',
  TODAY = 'today',
  ONE_DAY_OLD = 'oneDayOld',
  TWO_DAYS_OLD = 'twoDaysOld',
  THREE_DAYS_OLD = 'threeDaysOld',
  FOUR_DAYS_OLD = 'fourDaysOld',
  FIVE_DAYS_OLD = 'fiveDaysOld',
  MORE_THAN_FIVE_DAYS_OLD = 'moreThanFiveDaysOld',
}

export type SLA = {
  onlyShowMissedSLALastWorkingDay: boolean
  onlyShowMissedSLA2DaysInARow: boolean
  onlyShowMissedSLAAnyTime: boolean
}

export interface FraudSettings {
  hideAwaitingResponseOutcome: boolean
}

enum Cifas {
  ALL = 'all',
  ONLY = 'only',
  NONE = 'none',
}

enum TenancyStartDate {
  all = 'all',
  within1Day = 'within1Day',
  within2Days = 'within2Days',
  within3Days = 'within3Days',
  within5Days = 'within5Days',
  within10Days = 'within10Days',
}

export interface Filters {
  fraudSettings: FraudSettings
  sla: SLA
  ageOfReference: AgeOfReference
  cifas: Cifas
  tenancyStartDate: TenancyStartDate
}

export type QueryVars = {
  customerId: string
  limit: number
  offset: number
  filter: Filters
}

enum JourneyType {
  essential = 'essential',
  employer = 'employer',
  advanced = 'goodlord_advanced',
  advanced_unmapped = 'goodlord-advanced',
  corporate = 'corporate',
}

type Address = {
  nameOrNumber?: string
  line1?: string
  line2?: string
  city?: string
  country?: string
  postcode?: string
}

type Milestone = {
  event: string
  createdAt: string
}

export type Subject = {
  avatar: string
  customerId: string
  applicationId: string
  subjectId: string
  customerClientName: string
  name: string
  email: string
  type: string
  journeyType: JourneyType
  address: Address
  milestones: Milestone[]
  tenancyStartDate: string
  visitors: string[]
  referenceTier: ReferenceTier
  flaggedAsFraudAt: string
}

export type GetFraudSubjectsData = {
  getFraudSubjects: {
    total: number
    offset: number
    results: Subject[]
  }
}

export const useGetFraudSubjectsQuery: WrappedUseLazyQuery<
  GetFraudSubjectsData,
  QueryVars
> = options =>
  useLazyQuery<GetFraudSubjectsData, QueryVars>(getFraudSubjectsQuery, {
    ...options,
    client: agentClient,
  })
